import React, {
  useId,
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import {
  ArrowLeft,
  ClipboardList,
  History,
  Pen,
  Printer,
  Send,
  ShieldCheck,
  Undo2,
} from 'lucide-react';
import {
  Button,
  Card,
  CardContent,
  ConfirmationDialog,
  DropdownField,
  Label,
} from '@roger/ui-components';
import {
  GenericOrderForm,
  type GenericOrderFormHandle,
} from './GenericOrderForm';
import { CMS485OrderForm } from './CMS485OrderForm';
import { PHOOrderForm } from './PHOOrderForm';
import { OrderGoalsCard } from '../goals/OrderGoalsCard';
import { AutoSaveComments } from '../intake/shared';
import {
  TaskHeaderControls,
  TaskWorkflowStatusBadge,
} from '../task/TaskHeaderControls';
import {
  signTask,
  approveTask,
  breakTaskSignatures,
  getOrder,
  getOrderPdf,
  getTaskSignatures,
  getAvailableClinicians,
  updateOrder,
  updateEpisodeAssignments,
  updateTaskNotes,
  returnTaskToClinician,
  revertCheckoutOrder,
} from '../../services/api';
import { listAgencyClinicPhysiciansAgencyClinicsAgencyClinicIdPhysiciansGet as listClinicPhysicians } from '../../services/api/sdk.gen';
import type {
  OrderResponse,
  TaskSignatureResponse,
  AvailableClinicianResponse,
  ClinicPhysicianResponse,
} from '../../services/api/types.gen';
import { useStore } from '../../store';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import { useAllowSubmitWithoutSignature } from '../../hooks/useAllowSubmitWithoutSignature';
import { usePdfExport, type PdfSource } from '../../hooks/usePdfExport';
import { useToast } from '../../hooks/useToastContext';
import {
  formatDate,
  getSignButtonLabel,
  useTaskPermissions,
} from '../task/TaskDetailPanel';
import { AttestationDialog } from '../signing/AttestationDialog';
import type {
  ESignatureCredentialInput,
  SignatureDateInput,
} from '../signing/esignatureCredentialApi';
import { formatPhysicianName } from '../../utils/formatting';
import { cliniciansToSelectOptions } from '../../utils/clinicianUtils';
import { PermissionSlug } from '../../constants/featureFlags';
import { SignatureHistoryDialog } from '../signing/SignatureHistoryDialog';
import { WorkLogDialog } from '../task/WorkLogDialog';
import { useDeferredDialogOpener } from '../task/useDeferredDialogOpener';
import { ReadonlyPdfView } from '../documents/ReadonlyPdfView';
import EpisodeNotes from '../episode/EpisodeNotes';
import {
  resolveAttestationFamily,
  selectAttestationForFamily,
} from '../signing/attestations';
import { useActiveAttestations } from '../signing/useActiveAttestations';
import { signerName } from '../signing/signatureSummary';
import { EDITABLE_STATUSES } from '../../constants/statuses';
import { AutoSavePill } from '../AutoSavePill';
import type { AutoSaveStatus } from '../../hooks/useAutoSave';
import { useTaskSignatureBypasses } from '../signing/useTaskSignatureBypasses';
import { useSignatureBypassAction } from '../signing/useSignatureBypassAction';

interface OrderDetailPanelProps {
  orderId: string;
  patientId: string;
  onBack?: () => void;
  onVisitUpdated?: (visit: OrderResponse) => void;
  /**
   * This order is soft-deleted, opened from the deleted-tasks view. Widens the
   * reads that would otherwise 404 and makes the panel strictly read only: no
   * form, no assignment edits, no signing, no unlock. The Actions menu keeps
   * only print view, signature history and the work log.
   */
  isDeleted?: boolean;
}

type AssignmentField = 'clinic_physician_id' | 'case_manager_id';
type AssignmentState = Record<AssignmentField, string | null>;
type AssignmentSavingState = Record<AssignmentField, boolean>;

export const OrderDetailPanel: React.FC<OrderDetailPanelProps> = ({
  orderId,
  patientId,
  onBack,
  onVisitUpdated,
  isDeleted = false,
}) => {
  const user = useStore(state => state.user);
  const { error: showError } = useToast();
  const allowUnsignedMoves = useAllowSubmitWithoutSignature();
  const { exportPdf, exportingId } = usePdfExport();
  const physicianFieldId = useId();
  const caseManagerFieldId = useId();
  const {
    attestations,
    isLoading: attestationsLoading,
    error: attestationsError,
  } = useActiveAttestations();

  useEffect(() => {
    if (attestationsError) {
      showError(
        'Could not load signing text',
        'Refresh the page or try again in a moment.'
      );
    }
  }, [attestationsError, showError]);

  const [visit, setVisit] = useState<OrderResponse | null>(null);
  const [signatures, setSignatures] = useState<TaskSignatureResponse[]>([]);
  // ROG-3269: a bypassed chart must not render as normally signed. Keyed on an
  // explicit nonce rather than `signatures.length` — a bypass writes no
  // signature, so a count would never change and the notice would not appear
  // until the panel remounted.
  const [signatureRefreshNonce, setSignatureRefreshNonce] = useState(0);
  const [returning, setReturning] = useState(false);
  const [recalling, setRecalling] = useState(false);
  const [confirmRecallOpen, setConfirmRecallOpen] = useState(false);
  const recallRequestIdRef = useRef(0);
  const currentOrderIdRef = useRef(orderId);
  const mountedRef = useRef(false);
  currentOrderIdRef.current = orderId;
  const signatureBypasses = useTaskSignatureBypasses(
    orderId,
    signatureRefreshNonce
  );
  const [loading, setLoading] = useState(true);

  const [signingLoading, setSigningLoading] = useState(false);
  const [approvingLoading, setApprovingLoading] = useState(false);
  const [editingLoading, setEditingLoading] = useState(false);
  const [physicians, setPhysicians] = useState<ClinicPhysicianResponse[]>([]);
  const [caseManagers, setCaseManagers] = useState<
    AvailableClinicianResponse[]
  >([]);
  const genericFormRef = useRef<GenericOrderFormHandle>(null);
  // Mirrors the active editable form's autosave state so the pill can sit in a
  // shared sticky overlay (always visible) instead of scrolling away in the form
  // body. Reset on order switch so a prior order's "Saved" can't linger.
  const [saveStatus, setSaveStatus] = useState<AutoSaveStatus>('idle');
  // Mirrors the active form's edit counter so the pill's debounce progress bar
  // restarts in sync with each keystroke that resets the autosave timer.
  const [saveChangeToken, setSaveChangeToken] = useState(0);
  useEffect(() => {
    setSaveStatus('idle');
    setSaveChangeToken(0);
  }, [orderId]);
  useEffect(() => {
    recallRequestIdRef.current += 1;
    setRecalling(false);
    setConfirmRecallOpen(false);
  }, [orderId]);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      recallRequestIdRef.current += 1;
    };
  }, []);
  const handleSaveStatusChange = useCallback(
    (status: AutoSaveStatus, changeToken: number) => {
      setSaveStatus(status);
      setSaveChangeToken(changeToken);
    },
    []
  );
  const [pendingAssignments, setPendingAssignments] = useState<AssignmentState>(
    {
      clinic_physician_id: null,
      case_manager_id: null,
    }
  );
  const [savingAssignments, setSavingAssignments] =
    useState<AssignmentSavingState>({
      clinic_physician_id: false,
      case_manager_id: false,
    });
  const savingAssignmentsRef = useRef<AssignmentSavingState>({
    clinic_physician_id: false,
    case_manager_id: false,
  });
  const assignmentRequestIdsRef = useRef<Record<AssignmentField, number>>({
    clinic_physician_id: 0,
    case_manager_id: 0,
  });

  const setPendingAssignment = (
    field: AssignmentField,
    value: string | null
  ) => {
    setPendingAssignments(current => {
      return { ...current, [field]: value };
    });
  };

  const setAssignmentSaving = (field: AssignmentField, value: boolean) => {
    const next = { ...savingAssignmentsRef.current, [field]: value };
    savingAssignmentsRef.current = next;
    setSavingAssignments(next);
  };

  const hasSavingAssignment = () =>
    Object.values(savingAssignmentsRef.current).some(Boolean);

  const getAssignmentValue = (order: OrderResponse, field: AssignmentField) =>
    field === 'clinic_physician_id'
      ? (order.clinic_physician_id ?? null)
      : (order.episode_case_manager_id ?? null);

  const applyAssignmentToOrder = (
    order: OrderResponse,
    field: AssignmentField,
    value: string
  ): OrderResponse =>
    field === 'clinic_physician_id'
      ? { ...order, clinic_physician_id: value }
      : { ...order, episode_case_manager_id: value };

  // --- Data fetching ---

  const fetchData = async () => {
    try {
      setLoading(true);

      // Only the initial load is widened. The other refresh in this file
      // follows a mutation, and none of those can run on a deleted order.
      const [visitData, signaturesData] = await Promise.all([
        getOrder({
          path: { order_id: orderId },
          ...(isDeleted && { query: { include_deleted: true } }),
        }),
        getTaskSignatures({
          path: { task_id: orderId },
          ...(isDeleted && { query: { include_deleted: true } }),
        }),
      ]);

      if (visitData?.data) {
        setVisit(visitData.data as unknown as OrderResponse);
        onVisitUpdated?.(visitData.data as unknown as OrderResponse);
      }
      if (signaturesData?.data) {
        setSignatures(signaturesData.data);
      }
    } catch (err) {
      console.error('Error fetching order data:', err);
      showError('Load Failed', 'Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  // Saving the order's notes auto-advances a Not Started order to Saved on
  // the backend. Reflect that returned status locally instead of refetching,
  // which would reset the notes textarea mid-typing (useAutoSave re-syncs on
  // prop change).
  const saveOrderNotes = async (value: string) => {
    const res = await updateTaskNotes({
      path: { task_id: orderId },
      body: { notes: value.trim() || null },
    });
    const nextStatus = res.data?.status;
    if (nextStatus) {
      setVisit(prev => (prev ? { ...prev, status: nextStatus } : prev));
    }
  };

  // GenericOrderForm autosaves the order itself and hands back the fresh row.
  // Sync it into panel state and bubble it up WITHOUT calling fetchData(): a
  // full refetch flips `loading`, which unmounts the whole panel (see the
  // `if (loading)` guard below) and would drop the form's in-progress edits
  // mid-autosave. An order-data edit doesn't touch signatures, so there's
  // nothing else to reload here.
  const handleGenericOrderSaved = useCallback(
    (updatedOrder: OrderResponse) => {
      setVisit(updatedOrder);
      onVisitUpdated?.(updatedOrder);
    },
    [onVisitUpdated]
  );

  // An order the chart import carried over from the previous EMR. Its content
  // is that EMR's print, so it is read only at every status and the body shows
  // the print. An evaluation's signature request is Roger's own shape even when
  // imported, so it keeps the ordinary panel. Mirrors is_previous_emr_order on
  // the backend, which refuses the edits this hides.
  const isPreviousEmrOrder =
    !!visit?.imported_at && visit.order_type !== 'SIGNABLE_VISIT_ORDER';
  // Such an order keeps the previous EMR's own name ("Frequency Discontinue
  // Order"), which the import stores in order_summary. Mirrors
  // order_display_name on the backend.
  const orderTitle =
    (isPreviousEmrOrder &&
      visit?.order_summary?.split(/\s+/).filter(Boolean).join(' ')) ||
    visit?.order_type_display_name;

  // A deleted order is never editable, whatever status it was deleted in.
  // Deriving read-only from this (rather than only hiding controls) is what
  // keeps the order form and its autosave off the page entirely.
  const isReadonly =
    isDeleted ||
    isPreviousEmrOrder ||
    !EDITABLE_STATUSES.has(visit?.status ?? 'not_started');

  useEffect(() => {
    fetchData();
  }, [orderId, isDeleted]);

  useEffect(() => {
    const fetchClinicians = async () => {
      try {
        const [physRes, cmRes] = await Promise.all([
          visit?.patient_clinic_id
            ? listClinicPhysicians({
                path: { agency_clinic_id: visit.patient_clinic_id },
              })
            : Promise.resolve({ data: [] }),
          getAvailableClinicians({
            query: visit?.agency_clinic_id
              ? { clinic_id: visit.agency_clinic_id }
              : {},
          }),
        ]);
        if (physRes.data) {
          setPhysicians(physRes.data as ClinicPhysicianResponse[]);
        }
        if (cmRes.data) {
          setCaseManagers(cmRes.data.filter(c => c.is_case_manager));
        }
      } catch (err) {
        console.error('Failed to fetch clinicians:', err);
        showError(
          'Load Failed',
          'Failed to load physicians and case managers.'
        );
      }
    };
    if (!loading) fetchClinicians();
  }, [loading, visit?.agency_clinic_id, visit?.patient_clinic_id, showError]);

  const handleAssignmentChange = async (
    field: AssignmentField,
    value: string
  ) => {
    if (!visit) return;

    if (hasSavingAssignment() || value === getAssignmentValue(visit, field)) {
      return;
    }

    const requestId = assignmentRequestIdsRef.current[field] + 1;
    assignmentRequestIdsRef.current[field] = requestId;
    setPendingAssignment(field, value);
    setAssignmentSaving(field, true);

    try {
      const result =
        field === 'clinic_physician_id'
          ? await updateOrder({
              path: { order_id: visit.id },
              body: { clinic_physician_id: value },
            })
          : await updateEpisodeAssignments({
              path: { episode_id: visit.episode_id },
              body: { [field]: value },
            });
      if (result.error) {
        console.error('Failed to update assignment:', result.error);
        showError(
          'Update Failed',
          'Failed to update assignment. Please try again.'
        );
        return;
      }

      setVisit(current =>
        current ? applyAssignmentToOrder(current, field, value) : current
      );
      setPendingAssignment(field, null);

      try {
        await refreshVisitAndSignatures({ field, value });
      } catch (refreshErr) {
        console.error(
          'Failed to refresh order after assignment update:',
          refreshErr
        );
        showError(
          'Update Saved',
          'Assignment saved, but refreshed details could not be loaded. Please refresh if details look stale.'
        );
      }
    } catch (err) {
      console.error('Failed to update assignment:', err);
      showError(
        'Update Failed',
        'Failed to update assignment. Please try again.'
      );
    } finally {
      if (assignmentRequestIdsRef.current[field] === requestId) {
        setPendingAssignment(field, null);
        setAssignmentSaving(field, false);
      }
    }
  };

  // --- Derived permission state ---

  const {
    hasCaseManagerRole,
    isAssignedClinician,
    canSign: canSignTask,
    canApprove: canApproveTask,
    canBreakSignatures: canBreakTaskSignatures,
    signRequiresESignature,
    signTargetStatus,
    canBypassSignature: canBypassTaskSignature,
    canReturnToClinician: canReturnTaskToClinician,
  } = useTaskPermissions(user, visit, {
    allowSubmitWithoutSignature: allowUnsignedMoves,
  });

  // A deleted order forfeits every workflow action, regardless of role. Zeroed
  // here rather than at each use site so a new action added below inherits the
  // suppression instead of having to remember it.
  const canSign = !isDeleted && canSignTask;
  const canApprove = !isDeleted && canApproveTask;
  // A previous-EMR order cannot be reopened or handed back: there is nothing in
  // Roger to edit and re-sign.
  const canBreakSignatures =
    !isDeleted && !isPreviousEmrOrder && canBreakTaskSignatures;
  const canBypassSignature = !isDeleted && canBypassTaskSignature;
  const canReturnToClinician =
    !isDeleted && !isPreviousEmrOrder && canReturnTaskToClinician;
  // Who may correct the physician on a locked order. Deliberately NOT
  // canBreakSignatures: that answers which statuses /break-signatures accepts,
  // and an order reaches three locked statuses outside that set through
  // checkout and checkin (sent_to_physician, returned_with_signature,
  // sent_no_signature_required). Gating on it would leave those with no picker
  // for anybody, while the backend still accepts the correction. This asks the
  // authority question instead, which holds at every status.
  const canCorrectLockedPhysician =
    !isDeleted && (hasCaseManagerRole || isAssignedClinician);
  // The physician the previous EMR named on an imported order that matched no
  // directory physician. Shown until a case manager picks one, which sending
  // still requires.
  const previousEmrPhysicianLabel = visit?.external_physician_name
    ? `${visit.external_physician_name} (previous EMR)`
    : null;
  const assignedPhysicianName = useMemo(() => {
    // Branch on the id, not on the lookup. The clinic physician list is scoped
    // to the patient's clinic and can legitimately not contain this order's
    // physician (a clinic move, a 403 on the list, no patient clinic at all),
    // and it is empty on the first paint regardless. Saying "Not set" then
    // would assert the physician is missing on an order that has one, which is
    // exactly the fact this row exists to report.
    if (!visit?.clinic_physician_id)
      return previousEmrPhysicianLabel ?? 'Not set';
    const match = physicians.find(p => p.id === visit.clinic_physician_id);
    return match ? formatPhysicianName(match) : 'Unavailable';
  }, [physicians, visit?.clinic_physician_id, previousEmrPhysicianLabel]);

  const hasRequiredAssignments =
    !!visit?.clinic_physician_id && !!visit?.episode_case_manager_id;

  const activeSignatures = useMemo(
    () => signatures.filter(sig => !sig.invalidated_at),
    [signatures]
  );

  const signatureSummary = useMemo(
    () =>
      activeSignatures.map(signature => {
        const metadata =
          (signature.signature_metadata as Record<string, unknown> | null) ??
          {};
        const displayName = signerName(signature);
        const credentials = metadata.signer_credentials as
          string | undefined | null;
        const role = metadata.signer_role as string | undefined;

        return {
          id: signature.id,
          role:
            role === 'case_manager'
              ? 'Case Manager'
              : role === 'clinician'
                ? 'Physician'
                : 'Signer',
          name: credentials ? `${displayName}, ${credentials}` : displayName,
        };
      }),
    [activeSignatures]
  );

  // Status gate removed per ROG-1942 — comments enabled for case managers at all
  // order statuses. Backend enforces role authorization via PUT /tasks/{id}/notes.
  const canEditNotes = !isDeleted && hasCaseManagerRole;
  const hasPrimaryHeaderActions = canBreakSignatures || canSign || canApprove;
  const assignmentSaveInFlight = Object.values(savingAssignments).some(Boolean);

  // --- Refresh helpers ---

  const refreshVisitAndSignatures = async (expectedAssignment?: {
    field: AssignmentField;
    value: string;
  }): Promise<OrderResponse | null> => {
    const [visitData, signaturesData] = await Promise.all([
      getOrder({ path: { order_id: orderId } }),
      getTaskSignatures({ path: { task_id: orderId } }),
    ]);
    if (expectedAssignment) {
      if (visitData?.error) throw visitData.error;
      if (signaturesData?.error) throw signaturesData.error;
    }
    const refreshedVisit = visitData?.data
      ? (visitData.data as unknown as OrderResponse)
      : null;
    if (
      refreshedVisit &&
      expectedAssignment &&
      getAssignmentValue(refreshedVisit, expectedAssignment.field) !==
        expectedAssignment.value
    ) {
      throw new Error('Refreshed order did not include updated assignment.');
    }
    if (refreshedVisit) {
      setVisit(refreshedVisit);
      onVisitUpdated?.(refreshedVisit);
    }
    if (signaturesData?.data) {
      setSignatures(signaturesData.data);
    }
    setSignatureRefreshNonce(n => n + 1);
    return refreshedVisit;
  };

  // A departed clinician strands an order exactly as it strands a visit, and a
  // CMS-485 order is the case most likely to matter. Same ceremony, same gate.
  const { actionItems: bypassActionItems, dialog: bypassDialog } =
    useSignatureBypassAction({
      taskId: orderId,
      canBypass: canBypassSignature,
      onBypassed: async () => {
        await refreshVisitAndSignatures();
      },
    });

  // --- Action handlers ---

  const orderAttestation = useMemo(() => {
    if (!attestations) return null;
    return selectAttestationForFamily(
      attestations,
      resolveAttestationFamily('order', visit?.order_type ?? null)
    );
  }, [attestations, visit?.order_type]);
  const [signAttestationOpen, setSignAttestationOpen] = useState(false);
  const [approveAttestationOpen, setApproveAttestationOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const openDeferred = useDeferredDialogOpener();
  const [workLogOpen, setWorkLogOpen] = useState(false);
  const canViewWorkLog = useStore(state =>
    state.hasPermission(PermissionSlug.WORKLOG_VIEW)
  );
  const canRecallFromPhysician = useStore(state =>
    state.hasPermission(PermissionSlug.ORDER_MANAGER)
  );

  const handleSignAndSubmit = async () => {
    if (hasSavingAssignment()) return;

    // Flush any pending debounced auto-save so the backend sees the latest
    // field values before the sign call. CMS-485 and PHO own their own
    // pre-sign flow elsewhere; everything else (generic + summary forms)
    // routes through whichever form ref is currently mounted.
    const editableFormHandle =
      visit?.order_type === 'CMS_485' ||
      visit?.order_type === 'POST_HOSPITAL_ORDER'
        ? null
        : genericFormRef.current;
    if (editableFormHandle) {
      const outcome = await editableFormHandle.flush();
      // A non-'saved' flush means the latest edits did not persist: 'error'
      // already surfaced a toast from the form's onSave, and 'declined' leaves
      // the inline validation errors visible. Either way, do not sign stale
      // data or close out of the form.
      if (outcome !== 'saved') return;
      const isValid = await editableFormHandle.validate();
      if (!isValid) return;
    }
    setSignAttestationOpen(true);
  };

  const doSignAndSubmit = async (
    credential: ESignatureCredentialInput,
    signatureDate?: SignatureDateInput
  ) => {
    if (!orderAttestation) return;

    try {
      setSigningLoading(true);
      const result = await signTask({
        path: { task_id: orderId },
        body: {
          ...credential,
          ...signatureDate,
          attestation_version: orderAttestation.version,
        },
      });
      if (result.error) {
        console.error('Error signing order:', result.error);
        showError(
          'Sign Failed',
          getBusinessErrorMessage(result) ??
            'Failed to sign and submit. Please try again.'
        );
        return;
      }
      await refreshVisitAndSignatures();
      // Closed only on success. Leaving it open on failure keeps the entered
      // e-signature password, so a mistyped one can be corrected rather than
      // retyped from scratch on each of the five attempts before lockout.
      setSignAttestationOpen(false);
    } catch (err) {
      console.error('Error signing order:', err);
      showError('Sign Failed', 'Failed to sign and submit. Please try again.');
    } finally {
      setSigningLoading(false);
    }
  };

  const handleApprove = async () => {
    if (hasSavingAssignment()) return;

    setApproveAttestationOpen(true);
  };

  const doApprove = async (credential: ESignatureCredentialInput) => {
    if (!orderAttestation) return;

    try {
      setApprovingLoading(true);
      const result = await approveTask({
        path: { task_id: orderId },
        body: { ...credential, attestation_version: orderAttestation.version },
      });
      if (result.error) {
        console.error('Error approving order:', result.error);
        showError(
          'Approve Failed',
          getBusinessErrorMessage(result) ??
            'Failed to complete order. Please try again.'
        );
        return;
      }
      await refreshVisitAndSignatures();
      setApproveAttestationOpen(false);
    } catch (err) {
      console.error('Error approving order:', err);
      showError(
        'Approve Failed',
        'Failed to complete order. Please try again.'
      );
    } finally {
      setApprovingLoading(false);
    }
  };

  const handleBreakSignatures = async () => {
    try {
      setEditingLoading(true);
      const result = await breakTaskSignatures({
        path: { task_id: orderId },
      });
      if (result.error) {
        console.error('Error unlocking order:', result.error);
        showError('Unlock Failed', 'Failed to unlock order. Please try again.');
        return;
      }
      await refreshVisitAndSignatures();
    } catch (err) {
      console.error('Error unlocking order:', err);
      showError('Unlock Failed', 'Failed to unlock order. Please try again.');
    } finally {
      setEditingLoading(false);
    }
  };

  // ROG-3268 / EMR-1467 — a case manager hands the order back to the clinician
  // instead of approving it. Same endpoint and same gate as the visit panel;
  // the backend is task-generic, only the UI was visits-only.
  const handleReturnToClinician = async () => {
    try {
      setReturning(true);
      const result = await returnTaskToClinician({
        path: { task_id: orderId },
      });
      if (result.error) {
        console.error('Error returning order to clinician:', result.error);
        showError(
          'Return Failed',
          getBusinessErrorMessage(result) ??
            'Failed to return the order. Please try again.'
        );
        return;
      }
      await refreshVisitAndSignatures();
    } catch (err) {
      console.error('Error returning order to clinician:', err);
      showError(
        'Return Failed',
        'Failed to return the order. Please try again.'
      );
    } finally {
      setReturning(false);
    }
  };

  const handleRecallFromPhysician = async () => {
    const requestOrderId = orderId;
    const requestId = recallRequestIdRef.current + 1;
    recallRequestIdRef.current = requestId;
    const isCurrentRecall = () =>
      mountedRef.current &&
      recallRequestIdRef.current === requestId &&
      currentOrderIdRef.current === requestOrderId;

    try {
      setRecalling(true);
      const result = await revertCheckoutOrder({
        path: { order_id: requestOrderId },
      });
      if (!isCurrentRecall()) return;
      if (result.error) {
        console.error('Error recalling order from physician:', result.error);
        showError(
          'Move Failed',
          'Failed to move the order to To Be Sent. Please try again.'
        );
        return;
      }

      const committedOrder = result.data as unknown as
        OrderResponse | undefined;
      if (committedOrder) {
        setVisit(committedOrder);
      }

      try {
        const [visitData, signaturesData] = await Promise.all([
          getOrder({ path: { order_id: requestOrderId } }),
          getTaskSignatures({ path: { task_id: requestOrderId } }),
        ]);
        if (visitData?.error) throw visitData.error;
        if (signaturesData?.error) throw signaturesData.error;
        if (!visitData?.data || !signaturesData?.data) {
          throw new Error('Refreshed order details were incomplete.');
        }
        if (!isCurrentRecall()) return;

        const refreshedOrder = visitData.data as unknown as OrderResponse;
        setVisit(refreshedOrder);
        onVisitUpdated?.(refreshedOrder);
        setSignatures(signaturesData.data);
        setSignatureRefreshNonce(n => n + 1);
        setConfirmRecallOpen(false);
      } catch (refreshError) {
        if (!isCurrentRecall()) return;
        console.error('Failed to refresh order after recall:', refreshError);
        showError(
          'Recall Complete',
          'The order was recalled, but refreshed details could not be loaded. Please refresh if details look stale.'
        );
        if (committedOrder) onVisitUpdated?.(committedOrder);
        setConfirmRecallOpen(false);
      }
    } catch (err) {
      if (!isCurrentRecall()) return;
      console.error('Error recalling order from physician:', err);
      showError(
        'Move Failed',
        'Failed to move the order to To Be Sent. Please try again.'
      );
    } finally {
      if (isCurrentRecall()) setRecalling(false);
    }
  };

  // --- PDF export (opens in new tab) ---

  // Canonical CMS-485 preview for every locked order. GET /orders/{id}/pdf
  // already picks stored-vs-live. Status is the reload token: pending and
  // completed are both locked, so the iframe stays mounted across sign/bypass.
  const fetchOrderPdfBlob = useCallback(async (): Promise<Blob | null> => {
    const response = await getOrderPdf({
      path: { order_id: orderId },
      ...(isDeleted && { query: { include_deleted: true } }),
    });
    // Blob endpoints still populate `data` on HTTP errors (JSON/HTML body).
    // Treat `error` as failure so ReadonlyPdfView does not iframe that body
    // as a PDF.
    if (response.error || !response.data) {
      console.error('Failed to load order PDF:', response.error);
      return null;
    }
    return new Blob([response.data as BlobPart], { type: 'application/pdf' });
  }, [orderId, isDeleted, visit?.status]);

  const loadCanonicalOrderPdf = useCallback(async () => {
    const blob = await fetchOrderPdfBlob();
    if (!blob) return null;
    const url = URL.createObjectURL(blob);
    return { src: url, cleanup: () => URL.revokeObjectURL(url) };
  }, [fetchOrderPdfBlob]);

  const handleExportPdf = () => {
    if (!orderId) return;
    // GET /orders/{id}/pdf is now canonical: the backend resolves the right
    // artifact by order type (signed CMS-485 form, styled discharge/transfer
    // summary, or generic metadata), so this client no longer branches.
    void exportPdf(
      orderId,
      async (): Promise<PdfSource | null> => {
        const blob = await fetchOrderPdfBlob();
        return blob ? { blob } : null;
      },
      'order PDF',
      {
        // Mirror handleSignAndSubmit: flush any pending debounced auto-save so
        // the exported PDF reflects the latest field values. A non-'saved'
        // outcome means the edits did not persist ('error' already toasted from
        // the form's onSave, 'declined' leaves inline validation visible), so
        // abort the export rather than print stale, server-persisted data.
        preflight: async (): Promise<boolean> => {
          const editableFormHandle =
            visit?.order_type === 'CMS_485' ||
            visit?.order_type === 'POST_HOSPITAL_ORDER'
              ? null
              : genericFormRef.current;
          if (!editableFormHandle) return true;
          return (await editableFormHandle.flush()) === 'saved';
        },
      }
    );
  };

  // --- Render ---

  if (loading) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4' />
          <p className='text-muted-foreground'>Loading order details...</p>
        </div>
      </div>
    );
  }

  if (!visit) {
    return (
      <div className='flex items-center justify-center h-64'>
        <p className='text-muted-foreground'>Order not found.</p>
      </div>
    );
  }

  return (
    <div className='flex flex-col h-full'>
      {/* Header */}
      <div className='flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-5 py-3 border-b bg-muted/20'>
        <div className='flex min-w-0 flex-1 items-center gap-3'>
          {onBack && (
            <Button
              variant='outline'
              size='sm'
              onClick={onBack}
              className='flex items-center gap-1.5 flex-shrink-0'
            >
              <ArrowLeft className='h-3.5 w-3.5' />
              Back
            </Button>
          )}
          <div className='h-4 w-px bg-border' />
          <div className='min-w-0 flex-1'>
            <div className='flex min-w-0 items-center gap-2'>
              <span className='min-w-0 flex-1 truncate text-sm font-medium text-foreground'>
                {orderTitle}
              </span>
            </div>
            <div className='text-xs text-muted-foreground'>
              Order date: {formatDate(visit.order_date)}
            </div>
          </div>
        </div>

        <TaskHeaderControls
          className='w-full sm:w-auto'
          statusItems={
            <>
              {/* Status Badge — signature summary folds into its tooltip */}
              <TaskWorkflowStatusBadge
                status={visit.status}
                signatures={signatures}
                bypasses={signatureBypasses}
              />
            </>
          }
          primaryActions={
            hasPrimaryHeaderActions ? (
              <>
                {/* Unlock & Edit */}
                {canBreakSignatures && (
                  <Button
                    onClick={handleBreakSignatures}
                    disabled={editingLoading}
                    size='sm'
                    variant='outline'
                    className='flex items-center gap-1.5'
                  >
                    {editingLoading ? (
                      <>
                        <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-current' />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Pen className='h-4 w-4' />
                        {visit?.status === 'completed' ||
                        visit?.status === 'exported'
                          ? 'Reopen'
                          : 'Unlock & Edit'}
                      </>
                    )}
                  </Button>
                )}

                {/* Sign & Submit */}
                {canSign && (
                  <Button
                    onClick={handleSignAndSubmit}
                    disabled={
                      signingLoading ||
                      assignmentSaveInFlight ||
                      !hasRequiredAssignments ||
                      attestationsLoading ||
                      !orderAttestation
                    }
                    title={
                      !hasRequiredAssignments
                        ? 'Assign physician and case manager first'
                        : undefined
                    }
                    size='sm'
                    variant='default'
                    className='flex items-center gap-1.5'
                  >
                    {signingLoading ? (
                      <>
                        <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-current' />
                        Signing...
                      </>
                    ) : (
                      <>
                        <Send className='h-4 w-4' />
                        {getSignButtonLabel(signTargetStatus)}
                      </>
                    )}
                  </Button>
                )}

                {/* Sign & Complete */}
                {canApprove && (
                  <Button
                    onClick={handleApprove}
                    disabled={
                      approvingLoading ||
                      assignmentSaveInFlight ||
                      !hasRequiredAssignments ||
                      attestationsLoading ||
                      !orderAttestation
                    }
                    title={
                      !hasRequiredAssignments
                        ? 'Assign physician and case manager first'
                        : undefined
                    }
                    size='sm'
                    variant='default'
                    className='flex items-center gap-1.5'
                  >
                    {approvingLoading ? (
                      <>
                        <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-current' />
                        Completing...
                      </>
                    ) : (
                      <>
                        <ShieldCheck className='h-4 w-4' />
                        Sign & Complete
                      </>
                    )}
                  </Button>
                )}
              </>
            ) : undefined
          }
          secondaryActions={[
            {
              label:
                exportingId === orderId
                  ? 'Opening print view...'
                  : 'Print view',
              icon:
                exportingId === orderId ? (
                  <div
                    className='animate-spin rounded-full h-3 w-3 mr-2 border-b-2 border-current'
                    aria-hidden='true'
                  />
                ) : (
                  <Printer className='h-4 w-4 mr-2' aria-hidden='true' />
                ),
              onSelect: handleExportPdf,
              disabled: exportingId === orderId,
            },
            {
              label: 'View Signature History',
              icon: <History className='h-4 w-4 mr-2' aria-hidden='true' />,
              onSelect: () =>
                openDeferred('history', () => setHistoryOpen(true)),
            },
            ...(canViewWorkLog
              ? [
                  {
                    label: 'Work log',
                    icon: (
                      <ClipboardList
                        className='h-4 w-4 mr-2'
                        aria-hidden='true'
                      />
                    ),
                    onSelect: () =>
                      openDeferred('workLog', () => setWorkLogOpen(true)),
                  },
                ]
              : []),
            ...bypassActionItems,
            ...(canReturnToClinician
              ? [
                  {
                    label: returning ? 'Returning…' : 'Return to clinician',
                    icon: <Undo2 className='h-4 w-4 mr-2' aria-hidden='true' />,
                    onSelect: () => void handleReturnToClinician(),
                    disabled: returning,
                  },
                ]
              : []),
            ...(canRecallFromPhysician &&
            !isDeleted &&
            visit.status === 'sent_to_physician'
              ? [
                  {
                    label: recalling ? 'Recalling…' : 'Recall from Physician',
                    icon: <Undo2 className='h-4 w-4 mr-2' aria-hidden='true' />,
                    onSelect: () =>
                      openDeferred('recall', () => setConfirmRecallOpen(true)),
                    disabled: recalling,
                  },
                ]
              : []),
          ]}
        />
      </div>

      {/* The physician is metadata about the order rather than part of the form
          that gets signed, so it stays editable once the order locks. The case
          manager gives way to signer history there, and a deleted order forfeits
          every edit.

          Editing while unlocked is unchanged. The locked correction is offered
          only to the people who could unlock and edit anyway, so it is not a way
          around that authority. An affordance, not enforcement: see the
          permission decision in the PR body. */}
      <div className='flex items-center gap-4 px-5 py-2 border-b bg-muted/10'>
        {!isDeleted && isReadonly && !canCorrectLockedPhysician && (
          // Read-only for a viewer who may not correct it. Shown rather than
          // hidden: this row is the only place a locked order displays its
          // physician, so removing it would take the information away too.
          <div className='w-72 flex items-center gap-2'>
            <Label className='text-xs whitespace-nowrap'>Physician</Label>
            <span
              data-testid='physician-readonly'
              className='truncate text-sm text-foreground'
            >
              {assignedPhysicianName}
            </span>
          </div>
        )}
        {!isDeleted && (!isReadonly || canCorrectLockedPhysician) && (
          // The inline label shares this box with the control, so the readonly
          // row needs the extra width to keep a physician name from clipping.
          <div className={isReadonly ? 'w-72' : 'w-48'}>
            <DropdownField
              label='Physician'
              htmlFor={physicianFieldId}
              // Inline beside the signer rows so the picker and the signers
              // read as one row rather than sitting a line apart.
              labelPlacement={isReadonly ? 'left' : 'top'}
              sortOptions
              fieldSize='sm'
              placeholder={
                (!visit.clinic_physician_id && previousEmrPhysicianLabel) ||
                'Select physician'
              }
              searchPlaceholder='Search physicians...'
              enableFiltering
              isClearable={false}
              options={physicians.map(p => ({
                value: p.id,
                label: formatPhysicianName(p),
              }))}
              value={
                pendingAssignments.clinic_physician_id ??
                visit.clinic_physician_id ??
                ''
              }
              onChange={v => handleAssignmentChange('clinic_physician_id', v)}
              disabled={assignmentSaveInFlight}
            />
          </div>
        )}
        {isReadonly ? (
          signatureSummary.map(signature => (
            <div key={signature.id} className='flex items-center gap-2'>
              <Label className='text-xs text-muted-foreground whitespace-nowrap'>
                {signature.role} Signer
              </Label>
              <span className='text-sm'>{signature.name}</span>
            </div>
          ))
        ) : (
          <div className='w-48'>
            <DropdownField
              label='Episode Case Manager'
              htmlFor={caseManagerFieldId}
              sortOptions
              fieldSize='sm'
              placeholder='Select case manager'
              searchPlaceholder='Search case managers...'
              enableFiltering
              isClearable={false}
              options={cliniciansToSelectOptions(caseManagers)}
              value={
                pendingAssignments.case_manager_id ??
                visit.episode_case_manager_id ??
                ''
              }
              onChange={v => handleAssignmentChange('case_manager_id', v)}
              disabled={assignmentSaveInFlight}
            />
          </div>
        )}
      </div>

      {/* Save status pill: a zero-height sticky overlay above the scrollable
          body, floating over its top-right. Only the editable order forms
          generic report status; CMS 485 / PHO use a different save
          flow. Zero height means showing/hiding the pill never shifts layout,
          and the sticky positioning keeps it in view as the form scrolls. */}
      {!isReadonly && (
        <div className='pointer-events-none sticky top-0 z-20 flex h-0 shrink-0 justify-end px-5'>
          <AutoSavePill
            status={saveStatus}
            debounceMs={1500}
            changeToken={saveChangeToken}
          />
        </div>
      )}

      {/* Body */}
      <div className='flex-1 overflow-auto p-4'>
        {isReadonly ? (
          visit.order_type === 'CMS_485' || isPreviousEmrOrder ? (
            <div className='space-y-4'>
              {isPreviousEmrOrder && (
                <p
                  className='text-sm text-muted-foreground'
                  data-testid='previous-emr-order-note'
                >
                  This order came from the previous EMR. It shows as that EMR
                  printed it.
                </p>
              )}
              <div className='h-[calc(100vh-14rem)] min-h-[760px]'>
                <ReadonlyPdfView
                  loadSource={loadCanonicalOrderPdf}
                  title={
                    isPreviousEmrOrder
                      ? 'Previous EMR print'
                      : 'CMS 485 Preview'
                  }
                  errorText={
                    isPreviousEmrOrder
                      ? "The previous EMR's print for this order could not be shown."
                      : 'Failed to load this order. Please try again.'
                  }
                />
              </div>
              {canEditNotes ? (
                <Card>
                  <CardContent className='space-y-2'>
                    <span className='text-sm font-medium text-muted-foreground'>
                      Notes
                    </span>
                    <AutoSaveComments
                      value={visit.notes ?? ''}
                      onSave={saveOrderNotes}
                      placeholder='Add notes for this order...'
                    />
                  </CardContent>
                </Card>
              ) : visit.notes ? (
                <Card>
                  <CardContent className='space-y-2'>
                    <span className='text-sm font-medium text-muted-foreground'>
                      Notes
                    </span>
                    <div className='whitespace-pre-wrap rounded-md border bg-muted/30 p-3 text-sm'>
                      {visit.notes}
                    </div>
                  </CardContent>
                </Card>
              ) : null}
            </div>
          ) : (
            <>
              <Card>
                <CardContent className='p-4 space-y-4'>
                  {(() => {
                    const data = visit.order_data as Record<
                      string,
                      string
                    > | null;
                    const orderDate = visit.order_date || data?.order_date;
                    const orderTime = visit.order_time || data?.order_time;
                    const orderText = visit.order_text || data?.order_text;
                    const orderSummary =
                      visit.order_summary || data?.order_summary;
                    return (
                      <>
                        <div className='grid grid-cols-2 gap-4 text-sm'>
                          <div>
                            <span className='font-medium text-muted-foreground'>
                              Order Date
                            </span>
                            <p>{orderDate}</p>
                          </div>
                          {orderTime && (
                            <div>
                              <span className='font-medium text-muted-foreground'>
                                Order Time
                              </span>
                              <p>{orderTime}</p>
                            </div>
                          )}
                        </div>
                        {orderText && (
                          <div>
                            <span className='text-sm font-medium text-muted-foreground'>
                              Order Text
                            </span>
                            <div className='mt-1 whitespace-pre-wrap rounded-md border bg-muted/30 p-3 text-sm'>
                              {orderText}
                            </div>
                          </div>
                        )}
                        {orderSummary && (
                          <div>
                            <span className='text-sm font-medium text-muted-foreground'>
                              Order Summary
                            </span>
                            <div className='mt-1 whitespace-pre-wrap rounded-md border bg-muted/30 p-3 text-sm'>
                              {orderSummary}
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                  {canEditNotes ? (
                    <div>
                      <span className='text-sm font-medium text-muted-foreground'>
                        Notes
                      </span>
                      <AutoSaveComments
                        className='mt-1'
                        value={visit.notes ?? ''}
                        onSave={saveOrderNotes}
                        placeholder='Add notes for this order...'
                      />
                    </div>
                  ) : visit.notes ? (
                    <div>
                      <span className='text-sm font-medium text-muted-foreground'>
                        Notes
                      </span>
                      <div className='mt-1 whitespace-pre-wrap rounded-md border bg-muted/30 p-3 text-sm'>
                        {visit.notes}
                      </div>
                    </div>
                  ) : null}
                </CardContent>
              </Card>
              {visit.supports_goals && (
                <OrderGoalsCard
                  key={visit.id}
                  order={visit}
                  isReadonly={isDeleted}
                />
              )}
            </>
          )
        ) : (
          <>
            {visit.order_type === 'CMS_485' ? (
              <>
                <CMS485OrderForm visit={visit} patientId={patientId} />
                <Card className='mt-4'>
                  <CardContent className='space-y-2'>
                    <span className='text-sm font-medium text-muted-foreground'>
                      Notes
                    </span>
                    <AutoSaveComments
                      value={visit.notes ?? ''}
                      onSave={saveOrderNotes}
                      placeholder='Add notes for this order...'
                    />
                  </CardContent>
                </Card>
              </>
            ) : visit.order_type === 'POST_HOSPITAL_ORDER' ? (
              <>
                <PHOOrderForm visit={visit} patientId={patientId} />
                <Card className='mt-4'>
                  <CardContent className='space-y-2'>
                    <span className='text-sm font-medium text-muted-foreground'>
                      Notes
                    </span>
                    <AutoSaveComments
                      value={visit.notes ?? ''}
                      onSave={saveOrderNotes}
                      placeholder='Add notes for this order...'
                    />
                  </CardContent>
                </Card>
              </>
            ) : (
              <>
                <GenericOrderForm
                  ref={genericFormRef}
                  visit={visit}
                  onSave={handleGenericOrderSaved}
                  onSaveStatusChange={handleSaveStatusChange}
                />
                {canEditNotes && (
                  <Card className='mt-4'>
                    <CardContent className='space-y-2'>
                      <span className='text-sm font-medium text-muted-foreground'>
                        Notes
                      </span>
                      <AutoSaveComments
                        value={visit.notes ?? ''}
                        onSave={saveOrderNotes}
                        placeholder='Add notes for this order...'
                      />
                    </CardContent>
                  </Card>
                )}
              </>
            )}
            {/* Goals card lives below every order form variant, not just the
                generic one — every current goal-eligible order type
                (physician order, medication order) happens to use the
                generic form, but
                gating on `supports_goals` rather than the branch above keeps
                this correct if a specialized form ever supports goals too.
                It never touches GenericOrderForm's autosave/OCC state — its
                own GoalsTab manages independent loading/save state. */}
            {visit.supports_goals && (
              <OrderGoalsCard key={visit.id} order={visit} />
            )}
          </>
        )}

        {/* The episode's shared notes, once at the end of the body rather than
            inside each of the branches above, which render the order's own
            Notes card seven different ways. */}
        <Card className='mt-4'>
          <CardContent className='space-y-2'>
            <EpisodeNotes
              episodeId={visit.episode_id}
              canAdd={!isDeleted}
              description='Shared by every task in this episode.'
            />
          </CardContent>
        </Card>
      </div>

      <AttestationDialog
        open={signAttestationOpen}
        requiresCredential={signRequiresESignature}
        attestation={orderAttestation}
        signerName={user?.name ?? null}
        signerEmail={user?.email ?? null}
        isLoading={signingLoading}
        actionLabel={getSignButtonLabel(signTargetStatus)}
        // Clinician lane only -- see the prop's docs on AttestationDialog.
        showSignatureDate={signRequiresESignature}
        patientId={patientId}
        onConfirm={(credential, signatureDate) =>
          void doSignAndSubmit(credential, signatureDate)
        }
        onCancel={() => setSignAttestationOpen(false)}
      />

      <AttestationDialog
        open={approveAttestationOpen}
        requiresCredential={false}
        busyLabel='Approving…'
        attestation={orderAttestation}
        signerName={user?.name ?? null}
        signerEmail={user?.email ?? null}
        isLoading={approvingLoading}
        actionLabel='Approve & Complete'
        onConfirm={credential => void doApprove(credential)}
        onCancel={() => setApproveAttestationOpen(false)}
      />

      {bypassDialog}

      <SignatureHistoryDialog
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        taskId={orderId}
        includeDeleted={isDeleted}
      />

      <WorkLogDialog
        open={workLogOpen}
        onOpenChange={setWorkLogOpen}
        taskId={orderId}
        taskType='order'
        includeDeleted={isDeleted}
      />

      <ConfirmationDialog
        open={confirmRecallOpen}
        title='Recall this order?'
        message='This will move the order back to To Be Sent and clear its sent, received, and physician-signature dates. Only continue if the order needs to be sent again. It does not reopen the clinical documentation.'
        confirmLabel={recalling ? 'Recalling…' : 'Recall from Physician'}
        loading={recalling}
        onAccept={() => void handleRecallFromPhysician()}
        onCancel={() => setConfirmRecallOpen(false)}
      />
    </div>
  );
};
